import { useState } from "react";
import { Alert, Box, Stack, TextField, Typography } from "@mui/material";
import { useAuth } from "src/context/AuthContext";
import { useLang, useT } from "src/i18n";
import { resolveErrorMessage } from "src/i18n/errorCopy";
import BusyButton from "src/components/BusyButton";
import SocialLoginButtons from "src/components/SocialLoginButtons";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Shared step-1 component for the email-first auth modal. Probes
// /registration/check-email and bubbles the { isAvailable, hasPassword }
// result up to AuthBody, which picks the next sub-flow (register-code vs
// login-method) from the probe alone. There is no login/register "mode" here —
// the step does both, so the title reflects that ("Kirjaudu tai luo tili").
export default function EmailStep({ email, onEmailChange, onResult }) {
    const { checkEmail } = useAuth();
    const { lang } = useLang();
    const t = useT();
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        const trimmed = email.trim();
        if (!trimmed || !EMAIL_RE.test(trimmed)) {
            setError(t("auth.email.invalidEmail"));
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const result = await checkEmail(trimmed);
            onResult({ email: trimmed, ...result });
        } catch (err) {
            setError(resolveErrorMessage(err, { t, lang, fallbackKey: "auth.checkFailed" }));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box component="form" onSubmit={submit} sx={{ maxWidth: 480 }}>
            <Stack spacing={3}>
                <Typography variant="h4" component="h1">
                    {t("auth.email.title")}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {t("auth.email.intro")}
                </Typography>
                {error && <Alert severity="error">{error}</Alert>}
                <TextField
                    label={t("auth.shared.emailLabel")}
                    type="email"
                    required
                    fullWidth
                    autoFocus
                    value={email}
                    onChange={(e) => onEmailChange(e.target.value)}
                />
                <BusyButton
                    type="submit" size="large" variant="contained" color="warning" loading={busy}
                >
                    {busy ? t("auth.email.checking") : t("auth.email.continueButton")}
                </BusyButton>
                <SocialLoginButtons />
            </Stack>
        </Box>
    );
}
